import { CalendarDays } from "lucide-react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const ScheduleTab = ({ campaign }) => {
  const items = campaign.schedule || [];
  if (!items.length) {
    return (
      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-8 text-center" data-testid="tab-panel-schedule">
        <p className="text-sm text-zinc-400">No scheduled tasks for this campaign.</p>
      </div>
    );
  }

  const byDate = {};
  for (const item of items) {
    (byDate[item.date] = byDate[item.date] || []).push(item);
  }
  const firstDate = new Date(`${items[0].date}T00:00:00`);
  const year = firstDate.getFullYear();
  const month = firstDate.getMonth();
  const monthLabel = firstDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leadingBlanks = (new Date(year, month, 1).getDay() + 6) % 7; // Monday-first
  const todayIso = new Date().toISOString().slice(0, 10);

  const cells = [];
  for (let i = 0; i < leadingBlanks; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push(`${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  }

  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6" data-testid="tab-panel-schedule">
      <div className="flex items-center gap-3 mb-6">
        <CalendarDays className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
        <h3 className="font-display text-xl text-white">{monthLabel}</h3>
      </div>
      <div className="grid grid-cols-7 gap-1.5 mb-2">
        {WEEKDAYS.map((d) => (
          <span key={d} className="font-mono text-[9px] uppercase tracking-[0.16em] text-zinc-500 text-center py-1">{d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5" data-testid="schedule-grid">
        {cells.map((iso, i) => {
          if (!iso) return <span key={`blank-${i}`} className="min-h-[76px]" />;
          const dayItems = byDate[iso] || [];
          const isToday = iso === todayIso;
          return (
            <div key={iso}
              className={`min-h-[76px] rounded-md border p-1.5 ${dayItems.length ? "border-emerald-400/30 bg-emerald-400/5" : "border-white/5"} ${isToday ? "ring-1 ring-white/30" : ""}`}
              data-testid={dayItems.length ? `schedule-day-${iso}` : undefined}>
              <span className={`font-mono text-[10px] ${isToday ? "text-white" : "text-zinc-500"}`}>{Number(iso.slice(8))}</span>
              <div className="mt-1 space-y-1">
                {dayItems.map((item, j) => (
                  <div key={`${item.channel}-${j}`} className="rounded bg-white/5 border border-white/10 px-1.5 py-1" title={`${item.task} · ${item.time}`}>
                    <span className="block font-mono text-[8px] uppercase tracking-[0.12em] text-emerald-400 truncate">{item.channel}</span>
                    <span className="block text-[10px] text-zinc-300 leading-tight truncate">{item.time}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-6 pt-5 border-t border-white/10 space-y-2" data-testid="schedule-list">
        {items.map((item, i) => (
          <div key={`${item.channel}-list-${i}`} className="flex items-center gap-4 text-sm" data-testid={`schedule-row-${i}`}>
            <span className="font-mono text-xs text-zinc-400 w-32 shrink-0">{item.date} · {item.time}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-emerald-400 w-24 shrink-0">{item.channel}</span>
            <span className="text-zinc-300">{item.task}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
